import {
  type BlockAlias,
  BlockAliasRegistry,
  type BlockName,
  BlockRegistry,
} from '@core/block';
import { isValidMacroAppHostname } from '@core/util/macroAppUrl';
import { mergeRegister } from '@lexical/utils';
import { $createPasteNode, PasteNode } from '@macro-inc/lexical-core';
import { convertThemev2v3 } from '@theme/utils/themeMigrations';
import {
  parseThemeV2Json,
  parseThemeV3Json,
} from '@theme/utils/themeValidation';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  type LexicalEditor,
  PASTE_COMMAND,
} from 'lexical';
import { $insertNodesAndSplitList } from '../../utils';
import {
  INSERT_DOCUMENT_MENTION_COMMAND,
  INSERT_PR_MENTION_COMMAND,
  INSERT_THEME_MENTION_COMMAND,
} from '../mentions';

/**
 * Character threshold above which a plain-text paste collapses into a
 * block-level PasteNode (mirroring Anthropic's "pasted" chip) instead of
 * being inserted inline. Chosen to roughly match a few paragraphs of prose.
 */
export const LARGE_PASTE_CHAR_THRESHOLD = 1500;

type MacroAppUrlParsed = {
  isValid: boolean;
  id: string | undefined;
  block: BlockName | BlockAlias | undefined;
  params: Record<string, string> | undefined;
};
export type MentionLinkResolver = (url: string) =>
  | {
      id: string;
      block: BlockName | BlockAlias;
      params: Record<string, string>;
    }
  | undefined;

const IgnoredParams = new Set(['referral_code']);

const ValidBlockNames = [...BlockRegistry, ...BlockAliasRegistry];

export function parseMacroAppUrl(text: string): MacroAppUrlParsed {
  try {
    const url: URL = new URL(text);
    if (
      !url.pathname.startsWith('/app/') ||
      !isValidMacroAppHostname(url.hostname)
    ) {
      return {
        isValid: false,
        id: undefined,
        block: undefined,
        params: undefined,
      };
    }

    const pathParts: string[] = url.pathname.split('/').filter((part) => part);
    if (pathParts.length !== 3) {
      return {
        isValid: false,
        id: undefined,
        block: undefined,
        params: undefined,
      };
    }

    const routeBlock = pathParts[1] === 'agents' ? 'agent' : pathParts[1];
    const block = ValidBlockNames.find((name) => name === routeBlock);
    if (!block) {
      return {
        isValid: false,
        id: undefined,
        block: undefined,
        params: undefined,
      };
    }

    const idRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!idRegex.test(pathParts[2])) {
      return {
        isValid: false,
        id: undefined,
        block: undefined,
        params: undefined,
      };
    }

    const id: string = pathParts[2];
    const params: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
      if (IgnoredParams.has(key)) return;
      params[key] = value;
    });

    return {
      isValid: true,
      id: id,
      block: block,
      params: params,
    };
  } catch {
    return {
      isValid: false,
      id: undefined,
      block: undefined,
      params: undefined,
    };
  }
}

export function resolvePastedMacroAppUrl(
  text: string,
  resolveAppLink?: MentionLinkResolver
) {
  const legacy = parseMacroAppUrl(text);
  return legacy.isValid ? legacy : resolveAppLink?.(text);
}

function registerTextPastePlugin(
  editor: LexicalEditor,
  resolveAppLink?: MentionLinkResolver
) {
  return mergeRegister(
    editor.registerCommand(
      PASTE_COMMAND,
      (event: InputEvent | ClipboardEvent) => {
        if (event instanceof ClipboardEvent) {
          const pastedText: string =
            event.clipboardData?.getData('text/plain') || '';

          // Check for theme JSON before checking for Macro URL
          const themeV3 =
            parseThemeV3Json(pastedText) ??
            (() => {
              const legacy = parseThemeV2Json(pastedText);
              return legacy ? convertThemev2v3(legacy) : null;
            })();
          if (themeV3) {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && !selection.isCollapsed())
              return false;

            event.preventDefault();
            editor.dispatchCommand(INSERT_THEME_MENTION_COMMAND, {
              name: themeV3.name,
              data: themeV3,
            });
            return true;
          }

          const parsedMacroAppUrl = resolvePastedMacroAppUrl(
            pastedText,
            resolveAppLink
          );
          if (!parsedMacroAppUrl?.id || !parsedMacroAppUrl.block) {
            // Large plain-text pastes collapse into a block-level PasteNode
            // (Anthropic-style "pasted" chip). Only handle genuine plain text
            // pastes: defer to the richer paste handlers for HTML / Lexical
            // clipboards, and only when the cursor is a collapsed selection.
            const clipboard = event.clipboardData;
            const isRichClipboard = Boolean(
              clipboard?.getData('application/x-lexical-editor') ||
                clipboard?.getData('text/html')
            );
            if (
              !isRichClipboard &&
              editor.hasNode(PasteNode) &&
              pastedText.length > LARGE_PASTE_CHAR_THRESHOLD
            ) {
              const selection = $getSelection();
              if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                return false;
              }
              event.preventDefault();
              $insertNodesAndSplitList([
                $createPasteNode({ content: pastedText }),
              ]);
              return true;
            }
            return false;
          }

          const selection = $getSelection();
          if ($isRangeSelection(selection) && !selection.isCollapsed())
            return false;

          event.preventDefault();
          if (parsedMacroAppUrl.block === 'pr') {
            editor.dispatchCommand(INSERT_PR_MENTION_COMMAND, {
              id: parsedMacroAppUrl.id,
            });
            return true;
          }

          editor.dispatchCommand(INSERT_DOCUMENT_MENTION_COMMAND, {
            documentId: parsedMacroAppUrl.id,
            documentName: '',
            blockName: parsedMacroAppUrl.block,
            blockParams: parsedMacroAppUrl.params || {},
          });
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH
    )
  );
}

export function textPastePlugin(resolveAppLink?: MentionLinkResolver) {
  return (editor: LexicalEditor) =>
    registerTextPastePlugin(editor, resolveAppLink);
}
